import { z } from 'zod/v3';
import { BitbucketMetadataSchema } from '../integrations/config';

/**
 * Bitbucket Repository DTO
 */
export const BitbucketRepositorySchema = z.object({
  uuid: z.string(),
  name: z.string(),
  fullName: z.string(),
  slug: z.string(),
  description: z.string().optional(),
  isPrivate: z.boolean(),
  owner: z.object({
    uuid: z.string(),
    username: z.string(),
    displayName: z.string().optional(),
  }),
  workspace: z.object({
    slug: z.string(),
    name: z.string().optional(),
  }),
  mainbranch: z.object({
    name: z.string(),
  }).optional(),
  createdOn: z.string(),
  updatedOn: z.string(),
  size: z.number().optional(),
  language: z.string().optional(),
  hasIssues: z.boolean().optional(),
  hasWiki: z.boolean().optional(),
  links: z.object({
    html: z.object({
      href: z.string(),
    }),
    clone: z.array(z.object({
      name: z.string(),
      href: z.string(),
    })).optional(),
  }).optional(),
});

export type BitbucketRepositoryDTO = z.infer<typeof BitbucketRepositorySchema>;

/**
 * Bitbucket Pull Request DTO
 */
export const BitbucketPullRequestSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().optional(),
  state: z.enum(['OPEN', 'MERGED', 'DECLINED', 'SUPERSEDED']),
  author: z.object({
    uuid: z.string(),
    username: z.string(),
    displayName: z.string().optional(),
  }),
  source: z.object({
    branch: z.object({
      name: z.string(),
    }),
    commit: z.object({
      hash: z.string(),
    }),
    repository: z.object({
      name: z.string(),
      fullName: z.string(),
    }),
  }),
  destination: z.object({
    branch: z.object({
      name: z.string(),
    }),
    commit: z.object({
      hash: z.string(),
    }).optional(),
    repository: z.object({
      name: z.string(),
      fullName: z.string(),
    }),
  }),
  commentCount: z.number().optional(),
  taskCount: z.number().optional(),
  closeSourceBranch: z.boolean().optional(),
  createdOn: z.string(),
  updatedOn: z.string(),
  mergedBy: z.object({
    uuid: z.string(),
    username: z.string(),
    displayName: z.string().optional(),
  }).optional(),
  closedBy: z.object({
    uuid: z.string(),
    username: z.string(),
    displayName: z.string().optional(),
  }).optional(),
  reviewers: z.array(z.object({
    uuid: z.string(),
    username: z.string(),
    displayName: z.string().optional(),
    approved: z.boolean().optional(),
  })).optional(),
  participants: z.array(z.object({
    uuid: z.string(),
    username: z.string(),
    displayName: z.string().optional(),
    role: z.string().optional(),
    approved: z.boolean().optional(),
  })).optional(),
  links: z.object({
    html: z.object({
      href: z.string(),
    }),
  }).optional(),
});

export type BitbucketPullRequestDTO = z.infer<typeof BitbucketPullRequestSchema>;

/**
 * Bitbucket Issue DTO
 */
export const BitbucketIssueSchema = z.object({
  id: z.number(),
  title: z.string(),
  content: z.object({
    raw: z.string(),
    markup: z.string().optional(),
    html: z.string().optional(),
  }).optional(),
  state: z.enum(['new', 'open', 'resolved', 'on hold', 'invalid', 'duplicate', 'wontfix', 'closed']),
  kind: z.enum(['bug', 'enhancement', 'proposal', 'task']),
  priority: z.enum(['trivial', 'minor', 'major', 'critical', 'blocker']),
  reporter: z.object({
    uuid: z.string(),
    username: z.string(),
    displayName: z.string().optional(),
  }),
  assignee: z.object({
    uuid: z.string(),
    username: z.string(),
    displayName: z.string().optional(),
  }).optional(),
  component: z.object({
    name: z.string(),
  }).optional(),
  milestone: z.object({
    name: z.string(),
  }).optional(),
  version: z.object({
    name: z.string(),
  }).optional(),
  votes: z.number().optional(),
  watches: z.number().optional(),
  createdOn: z.string(),
  updatedOn: z.string(),
  editedOn: z.string().optional(),
  links: z.object({
    html: z.object({
      href: z.string(),
    }),
  }).optional(),
});

export type BitbucketIssueDTO = z.infer<typeof BitbucketIssueSchema>;

/**
 * Bitbucket Workspace DTO
 */
export const BitbucketWorkspaceSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  slug: z.string(),
  isPrivate: z.boolean().optional(),
  createdOn: z.string().optional(),
  updatedOn: z.string().optional(),
  links: z.object({
    html: z.object({
      href: z.string(),
    }),
  }).optional(),
});

export type BitbucketWorkspaceDTO = z.infer<typeof BitbucketWorkspaceSchema>;

/**
 * Bitbucket Branch/Tag DTO
 */
export const BitbucketRefSchema = z.object({
  name: z.string(),
  type: z.enum(['branch', 'tag']),
  target: z.object({
    hash: z.string(),
    date: z.string().optional(),
    message: z.string().optional(),
    author: z.object({
      user: z.object({
        uuid: z.string().optional(),
        username: z.string().optional(),
        displayName: z.string().optional(),
      }).optional(),
    }).optional(),
  }),
  links: z.object({
    html: z.object({
      href: z.string(),
    }).optional(),
    commits: z.object({
      href: z.string(),
    }).optional(),
  }).optional(),
});

export type BitbucketRefDTO = z.infer<typeof BitbucketRefSchema>;